import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { createHotel, updateHotel } from "@/lib/hotelsApi";
import { useToast } from "@/hooks/use-toast";
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";
import { NumericInput } from "@/components/ui/numeric-input";
import { MoneyInput } from "@/components/ui/money-input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, ChevronDown, ChevronUp } from "lucide-react";

const formSchema = z.object({
  hotel_name: z.string().min(1, "Hotel name is required"),
  location: z.string().optional(),
  address: z.string().optional(),
  stars: z.string().optional(),
  image_url: z.string().optional(),
  description: z.string().optional(),
  amenities: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

type Room = {
  id?: number;
  room_name: string;
  room_description: string;
  price_room_only: string;
  price_breakfast: string;
  price_half_board: string;
  price_full_board: string;
  max_occupancy: string;
  room_image_url: string;
};

interface HotelFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialData?: any;
  onSuccess: () => void;
  viewMode?: boolean;
}

const HotelForm = ({ open, onOpenChange, initialData, onSuccess, viewMode = false }: HotelFormProps) => {
  const { toast } = useToast();
  const [rooms, setRooms] = useState<Room[]>([
    {
      id: undefined,
      room_name: "",
      room_description: "",
      price_room_only: "",
      price_breakfast: "",
      price_half_board: "",
      price_full_board: "",
      max_occupancy: "",
      room_image_url: "",
    },
  ]);
  const [expandedRooms, setExpandedRooms] = useState<Record<number, boolean>>({});

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      hotel_name: "",
      location: "",
      address: "",
      stars: "",
      image_url: "",
      description: "",
      amenities: "",
    },
  });

  const {
    handleOpenChange: handleUnsavedOpenChange,
    ConfirmDialog,
    pendingClose,
    setPendingClose,
  } = useUnsavedChanges({
    isDirty: form.formState.isDirty,
    onSave: () => form.handleSubmit(onSubmit)(),
    onDiscard: () => {
      form.reset();
      setRooms([
        {
          id: undefined,
          room_name: "",
          room_description: "",
          price_room_only: "",
          price_breakfast: "",
          price_half_board: "",
          price_full_board: "",
          max_occupancy: "",
          room_image_url: "",
        },
      ]);
      onOpenChange(false);
    },
  });

  useEffect(() => {
    if (pendingClose) {
      onOpenChange(false);
      setPendingClose(false);
    }
  }, [pendingClose, onOpenChange, setPendingClose]);

  useEffect(() => {
    if (initialData) {
      form.reset({
        hotel_name: initialData.hotel_name || "",
        location: initialData.location || "",
        address: initialData.address || "",
        stars: initialData.stars?.toString() || "",
        image_url: initialData.image_url || "",
        description: initialData.description || "",
        amenities: initialData.amenities || "",
      });
      if (initialData.rooms && initialData.rooms.length > 0) {
        setRooms(
          initialData.rooms.map((room: any) => ({
            id: room.id,
            room_name: room.room_name || "",
            room_description: room.room_description || "",
            price_room_only: room.price_room_only?.toString() || "",
            price_breakfast: room.price_breakfast?.toString() || "",
            price_half_board: room.price_half_board?.toString() || "",
            price_full_board: room.price_full_board?.toString() || "",
            max_occupancy: room.max_occupancy?.toString() || "",
            room_image_url: room.room_image_url || "",
          }))
        );
      }
    } else {
      form.reset();
      setRooms([
        {
          id: undefined,
          room_name: "",
          room_description: "",
          price_room_only: "",
          price_breakfast: "",
          price_half_board: "",
          price_full_board: "",
          max_occupancy: "",
          room_image_url: "",
        },
      ]);
    }
  }, [initialData, form]);

  const addRoom = () => {
    setRooms([
      {
        id: undefined,
        room_name: "",
        room_description: "",
        price_room_only: "",
        price_breakfast: "",
        price_half_board: "",
        price_full_board: "",
        max_occupancy: "",
        room_image_url: "",
      },
      ...rooms,
    ]);
    setExpandedRooms({ ...expandedRooms, 0: true });
  };

  const removeRoom = (index: number) => {
    setRooms(rooms.filter((_, i) => i !== index));
    const newExpanded = { ...expandedRooms };
    delete newExpanded[index];
    setExpandedRooms(newExpanded);
  };

  const toggleRoomExpanded = (index: number) => {
    setExpandedRooms({
      ...expandedRooms,
      [index]: !expandedRooms[index],
    });
  };

  const updateRoom = (index: number, field: keyof Room, value: string) => {
    const newRooms = [...rooms];
    newRooms[index][field] = value;
    setRooms(newRooms);
  };

  const parseNumber = (value: string): number | null => {
    const trimmed = value?.trim();
    if (!trimmed) return null;
    const num = Number(trimmed);
    return Number.isNaN(num) ? null : num;
  };

  const onSubmit = async (data: FormData) => {
    try {
      const hotelPayload = {
        hotel_name: data.hotel_name,
        location: data.location || null,
        address: data.address || null,
        stars: data.stars ? parseInt(data.stars) : null,
        image_url: data.image_url || null,
        description: data.description || null,
        amenities: data.amenities || null,
        rooms: rooms
          .filter((room) => room.room_name.trim() !== "")
          .map((room) => ({
            id: room.id,
            room_name: room.room_name,
            room_description: room.room_description || null,
            price_room_only: parseNumber(room.price_room_only),
            price_breakfast: parseNumber(room.price_breakfast),
            price_half_board: parseNumber(room.price_half_board),
            price_full_board: parseNumber(room.price_full_board),
            max_occupancy: room.max_occupancy ? parseInt(room.max_occupancy) : null,
            room_image_url: room.room_image_url || null,
          })),
      };

      if (initialData?.id) {
        await updateHotel(initialData.id, hotelPayload);
      } else {
        await createHotel(hotelPayload);
      }

      toast({
        title: "Success",
        description: `Hotel ${initialData ? "updated" : "created"} successfully`,
      });
      form.reset();
      onSuccess();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (!open) {
      const shouldClose = handleUnsavedOpenChange(open);
      if (shouldClose) {
        onOpenChange(false);
      }
    } else {
      onOpenChange(open);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {viewMode ? "View Hotel" : initialData ? "Edit Hotel" : "Add New Hotel"}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {viewMode && form.watch("image_url") && (
              <div className="rounded-md overflow-hidden">
                <img
                  src={form.watch("image_url")}
                  alt={form.watch("hotel_name") || "Hotel"}
                  className="w-full h-64 object-cover"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="hotel_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Hotel Name *</FormLabel>
                    <FormControl>
                      <Input {...field} disabled={viewMode} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="location"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Location</FormLabel>
                    <FormControl>
                      <Input {...field} disabled={viewMode} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Address</FormLabel>
                  <FormControl>
                    <Textarea {...field} disabled={viewMode} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="stars"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Stars</FormLabel>
                    <FormControl>
                      <Input type="number" min="1" max="5" {...field} disabled={viewMode} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="image_url"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Image URL</FormLabel>
                    <FormControl>
                      <Input {...field} disabled={viewMode} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea {...field} disabled={viewMode} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="amenities"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Amenities</FormLabel>
                    <FormControl>
                      <Textarea {...field} disabled={viewMode} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="space-y-4 border-t pt-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Hotel Rooms</h3>
                {!viewMode && (
                  <Button type="button" size="sm" onClick={addRoom}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Room
                  </Button>
                )}
              </div>

              {rooms.map((room, index) => {
                const isExpanded = expandedRooms[index];
                const roomDisplayName = room.room_name || `Room ${index + 1}`;
                
                return (
                  <div key={index} className="border rounded-lg overflow-hidden">
                    <div 
                      className="p-4 bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => toggleRoomExpanded(index)}
                    >
                      <div className="flex items-center gap-3">
                        {room.room_image_url && (
                          <img 
                            src={room.room_image_url} 
                            alt={roomDisplayName}
                            className="w-12 h-12 object-cover rounded"
                          />
                        )}
                        <div className="flex-1">
                          <h4 className="font-medium">{roomDisplayName}</h4>
                          {!isExpanded && room.max_occupancy && (
                            <p className="text-sm text-muted-foreground">Max Occupancy: {room.max_occupancy}</p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <div className="flex items-center justify-center w-8 h-8">
                            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </div>
                          {!viewMode && rooms.length > 1 && (
                            <Button
                              type="button"
                              variant="destructive"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeRoom(index);
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="p-4 space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium">Room Name</label>
                            <Input
                              value={room.room_name}
                              onChange={(e) => updateRoom(index, "room_name", e.target.value)}
                              disabled={viewMode}
                            />
                          </div>
                          <div>
                            <label className="text-sm font-medium">Max Occupancy</label>
                            <NumericInput
                              value={room.max_occupancy}
                              onChange={(value) => updateRoom(index, "max_occupancy", value)}
                              allowDecimals={false}
                              disabled={viewMode}
                            />
                          </div>
                        </div>

                        <div>
                          <label className="text-sm font-medium">Room Description</label>
                          <Textarea
                            value={room.room_description}
                            onChange={(e) => updateRoom(index, "room_description", e.target.value)}
                            disabled={viewMode}
                          />
                        </div>

                        <div className="grid grid-cols-4 gap-4">
                          <div>
                            <label className="text-sm font-medium">Price (Room Only)</label>
                            <MoneyInput
                              value={room.price_room_only}
                              onChange={(value) => updateRoom(index, "price_room_only", value)}
                              className="w-[140px]"
                              disabled={viewMode}
                            />
                          </div>
                          <div>
                            <label className="text-sm font-medium">Price (Breakfast)</label>
                            <MoneyInput
                              value={room.price_breakfast}
                              onChange={(value) => updateRoom(index, "price_breakfast", value)}
                              className="w-[140px]"
                              disabled={viewMode}
                            />
                          </div>
                          <div>
                            <label className="text-sm font-medium">Price (Half Board)</label>
                            <MoneyInput
                              value={room.price_half_board}
                              onChange={(value) => updateRoom(index, "price_half_board", value)}
                              className="w-[140px]"
                              disabled={viewMode}
                            />
                          </div>
                          <div>
                            <label className="text-sm font-medium">Price (Full Board)</label>
                            <MoneyInput
                              value={room.price_full_board}
                              onChange={(value) => updateRoom(index, "price_full_board", value)}
                              className="w-[140px]"
                              disabled={viewMode}
                            />
                          </div>
                        </div>

                        <div>
                          <label className="text-sm font-medium">Room Image URL</label>
                          <Input
                            value={room.room_image_url}
                            onChange={(e) => updateRoom(index, "room_image_url", e.target.value)}
                            disabled={viewMode}
                          />
                        </div>

                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {viewMode ? "Close" : "Cancel"}
              </Button>
              {!viewMode && <Button type="submit">Save</Button>}
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
    <ConfirmDialog />
    </>
  );
};

export default HotelForm;
